import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  useMediaQuery,
  Menu,
  ListItemIcon,
} from '@mui/material';
import { Add, Delete, Edit, MoreVert, Translate } from '@mui/icons-material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  getAllInboxMessages,
  updateSafeguardMessage,
  deleteInboxMessage,
  addInboxMessage,
} from '../../../repository/AppInboxDAO';
import { useUserContext } from '../../../context_providers/user_context/UserProvider';
import EditAppInboxModal from './modals/EditAppInboxModal';
import ConfirmModal from '../../../components/modals/utils/ConfirmModal';
import CreateAppMessageModal from './modals/CreateAppMessageModal';
import Loading from '../../../components/loading/Loading';
import { useToast } from '../../../context_providers/toast_context/ToastContext';
import { ToastType } from '../../../context_providers/toast_context/ToastContext';
import LoadingSpinner from '../../../components/loading/LoadingSpinner';
import COLORS from '../../../resources/style/Themes';
import DefaultIconButton from '../../../components/utils/DefaultIconButton';
import MissingTranslationsModal from './modals/MissingTranslationsModal';
import {
  getMissingTranslations as fetchMissingTranslations,
  INBOX_MESSAGE_OBJECT_TYPE,
} from '../../../repository/TranslationDAO';
import { getAllLanguageCodes } from '../../../repository/LanguageCodesDAO';
import LoadingData from '../../../components/loading/LoadingData';
import {
  InboxMessageOnAzure,
  TranslationDraft,
} from '../../../../generated-client/models';

export const AppInbox = () => {
  const [responseData, setResponseData] = useState<InboxMessageOnAzure[]>([]);
  const [filteredData, setFilteredData] = useState<InboxMessageOnAzure[]>([]);
  const { bearerToken } = useUserContext();
  const [filters, setFilters] = useState({ language_code: '', name: '' });
  const [messageToEdit, setMessageToEdit] = useState<InboxMessageOnAzure>();
  const [messageToHighlight, setMessageToHighlight] = useState<
    string | null | undefined
  >(null);
  const [showEditMessageModal, setShowEditMessageModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [messageToRemove, setMessageToRemove] = useState<
    InboxMessageOnAzure | undefined
  >();
  const [showAddMessageModal, setShowAddMessageModal] = useState(false);
  const { showToast } = useToast();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);
  const [openTranslationsModal, setOpenTranslationsModal] = useState(false);
  const [translations, setTranslations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [languageCodes, setLanguageCodes] = useState<{
    [key: string]: string;
  }>({});
  const [currentItem, setCurrentItem] = useState<InboxMessageOnAzure | null>(
    null,
  );
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const [currentTranslatingLanguage, setCurrentTranslatingLanguage] =
    useState('');

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (messageToHighlight != null) {
      setTimeout(() => {
        setMessageToHighlight(null);
      }, 2000);
    }
  }, [messageToHighlight]);

  useEffect(() => {
    const fetchInboxData = async () => {
      try {
        const newData = await getAllInboxMessages();
        setResponseData(newData);
        setFilteredData(newData);
      } catch (e) {
        showToast(
          'Failed to fetch inbox data. Please try again.',
          ToastType.DANGER,
        );
      }
    };
    fetchInboxData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const applyFilters = () => {
      let data = [...responseData];
      if (filters.language_code) {
        data = data.filter(
          (item) => item.language_code === filters.language_code,
        );
      }
      if (filters.name) {
        data = data.filter((item) => item.name === filters.name);
      }
      setFilteredData(data);
    };
    applyFilters();
  }, [filters, responseData]);

  useEffect(() => {
    const fetchLanguageCodes = async () => {
      try {
        const codes = await getAllLanguageCodes();
        setLanguageCodes(codes);
      } catch (e) {
        showToast(
          `Failed to fetch language codes. Please try again.`,
          ToastType.DANGER,
        );
      }
    };
    fetchLanguageCodes();
  }, [bearerToken]);

  const updateMessage = async (editedMessage: InboxMessageOnAzure) => {
    try {
      await updateSafeguardMessage(editedMessage);

      const index = responseData.findIndex(
        (message) => message.id === editedMessage.id,
      );
      const updatedData = [...responseData];
      updatedData[index] = editedMessage;
      setResponseData(updatedData);
      showToast(
        `Successfully updated ${editedMessage.header} message.`,
        ToastType.SUCCESS,
      );
      setMessageToHighlight(editedMessage.id);
    } catch (e) {
      showToast(`Failed to update ${editedMessage.header}`, ToastType.DANGER);
    }
  };

  const deleteMessage = async () => {
    setShowConfirmModal(false);

    if (!messageToRemove) {
      showToast(
        'That message does not exist. Try refreshing your page.',
        ToastType.DANGER,
      );
      console.error("Tried to remove a message that doesn't exist");
      return;
    }

    try {
      await deleteInboxMessage(messageToRemove);
      const newResponseData = responseData.filter(
        (message) => message.id !== messageToRemove?.id,
      );
      setResponseData(newResponseData);
      showToast(
        `Successfully removed message ${messageToRemove?.header}.`,
        ToastType.SUCCESS,
      );
    } catch (e) {
      showToast(
        `Failed to remove message ${messageToRemove?.header}. Please try again.`,
        ToastType.DANGER,
      );
    } finally {
      setMessageToRemove(undefined);
    }
  };

  const closeTranslationsModal = () => {
    setOpenTranslationsModal(false);
    setTranslations([]);
    setCurrentItem(null);
    setCurrentTranslatingLanguage('');
  };

  const addMessage = async (newMessage: InboxMessageOnAzure) => {
    setLoadingAPIData(true);
    try {
      const createdMessage = await addInboxMessage(newMessage);
      showToast(
        `Successfully added ${newMessage.header} configuration.`,
        ToastType.SUCCESS,
      );
      setMessageToHighlight(newMessage.id);
      setResponseData((prevData) => [...prevData, createdMessage]);
    } catch (error) {
      console.error(error);
      showToast(
        `Failed to add ${newMessage.header} message. Please try again.`,
        ToastType.DANGER,
      );
    } finally {
      setLoadingAPIData(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  const renderFilters = () => (
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel htmlFor="language-filter">Language Code</InputLabel>
        <Select
          aria-label="Language Filter"
          id="language-filter"
          value={filters.language_code}
          onChange={(e) => handleFilterChange('language_code', e.target.value)}
          label="Language Code"
        >
          <MenuItem value="">All</MenuItem>
          {[...new Set(responseData.map((item) => item.language_code))].map(
            (code) => (
              <MenuItem value={code ?? ''} key={code ?? 'empty'}>
                {code}
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel htmlFor="name-filter">Name</InputLabel>
        <Select
          aria-label="Name Filter"
          id="name-filter"
          value={filters.name}
          onChange={(e) => handleFilterChange('name', e.target.value)}
          label="name"
        >
          <MenuItem value="">All</MenuItem>
          {[...new Set(responseData.map((item) => item.name))].map((name) => (
            <MenuItem value={name} key={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
  const renderData = () => (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          mb: 2,
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        {/* Title */}
        <Typography variant="h5" gutterBottom sx={{ flexShrink: 0 }}>
          App Inbox
        </Typography>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexGrow: 1,
            mx: 2,
          }}
        >
          {renderFilters()}
        </Box>

        {/* Add Button */}
        {isMobile ? (
          <>
            <IconButton onClick={handleMenuClick}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  setShowAddMessageModal(true);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <Add />
                </ListItemIcon>
                Add New
              </MenuItem>
            </Menu>
          </>
        ) : (
          <DefaultIconButton
            disabled={false}
            action={() => setShowAddMessageModal(true)}
            icon={<Add />}
            label="Add New"
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>
      <Typography variant="h5" color="error" align="center" gutterBottom>
        IMPORTANT! MESSAGES WILL BE VISIBLE TO USERS WITHIN THE COMPASS PRO
        MOBILE APPLICATION IMMEDIATELY ON ADD OR EDIT!
      </Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Header</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Language Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>URL</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                Translations
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                Edit
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                Remove
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((data, index) => (
              <TableRow
                key={index}
                className={data.id === messageToHighlight ? 'flash-green' : ''}
              >
                <TableCell>{data.header}</TableCell>
                <TableCell>{data.name}</TableCell>
                <TableCell>{data.message}</TableCell>
                <TableCell>{data.language_code}</TableCell>
                <TableCell>{data.url}</TableCell>
                <TableCell align="center">
                  {checkMissingTranslations(
                    data.name ?? '',
                    data.language_code ?? '',
                  ) && (
                    <Tooltip title="Missing translations">
                      <IconButton
                        aria-label="Missing translations"
                        onClick={() => {
                          setCurrentItem(data);
                          setOpenTranslationsModal(true);
                          getMissingTranslations(data);
                        }}
                        sx={{
                          borderRadius: '10px',
                          width: '40px',
                          height: '40px',
                          backgroundColor: COLORS.LIGHT_BLUE,
                          '&:hover': { backgroundColor: COLORS.HOVER_BLUE },
                        }}
                      >
                        <Translate sx={{ color: COLORS.DARK_BLUE }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit Message">
                    <IconButton
                      onClick={() => {
                        setMessageToEdit(data);
                        setShowEditMessageModal(true);
                      }}
                      sx={{
                        borderRadius: '10px',
                        width: '40px',
                        height: '40px',
                        backgroundColor: COLORS.LIGHT_GREEN,
                        '&:hover': { backgroundColor: COLORS.HOVER_GREEN },
                      }}
                    >
                      <Edit sx={{ color: COLORS.DARK_GREEN }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Delete Message">
                    <IconButton
                      aria-label="Delete Message"
                      onClick={() => {
                        setConfirmTitle(`Delete ${data.header}`);
                        setConfirmMessage(
                          `Are you sure you would like to delete message ${data.header}?`,
                        );
                        setMessageToRemove(data);
                        setShowConfirmModal(true);
                      }}
                      sx={{
                        borderRadius: '10px',
                        width: '40px',
                        height: '40px',
                        backgroundColor: COLORS.PINK,
                        '&:hover': { backgroundColor: COLORS.HOVER_RED },
                      }}
                    >
                      <Delete sx={{ color: COLORS.DARK_RED }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const getMissingTranslations = async (item: InboxMessageOnAzure) => {
    setIsLoading(true);
    try {
      const response = await fetchMissingTranslations({
        object_type: INBOX_MESSAGE_OBJECT_TYPE,
        id: item.id,
      });
      const drafts = response.drafts ?? [];
      if (drafts.length === 0) {
        closeTranslationsModal();
        return;
      }

      setTranslations(
        drafts.map((draft: TranslationDraft) => {
          const isError = (draft.status ?? '').toLowerCase() === 'error';
          if (isError) {
            return {
              languageName: draft.language_name ?? '',
              languageCode: draft.language_code ?? '',
              error: draft.error || 'Failed to translate',
              status: 'error',
            };
          }

          const translatedHeader = draft.header ?? '';
          const translatedMessage = draft.message ?? '';
          return {
            languageName: draft.language_name ?? '',
            languageCode: draft.language_code ?? '',
            translatedHeader,
            translatedMessage,
            original: {
              header: item.header,
              message: item.message,
            },
            newInboxMessage: {
              header: translatedHeader,
              message: translatedMessage,
              language_code: draft.language_code ?? '',
              name: draft.name ?? item.name,
              url: draft.url ?? item.url,
            },
            status: 'success',
          };
        }),
      );
    } catch (error) {
      closeTranslationsModal();
      showToast(
        'Failed to fetch translations. Please try again.',
        ToastType.DANGER,
      );
    } finally {
      setIsLoading(false);
      setCurrentTranslatingLanguage('');
    }
  };

  const checkMissingTranslations = (name: string, languageCode: string) => {
    if (
      !Object.keys(languageCodes).length ||
      languageCode.toLowerCase() !== 'eng'
    )
      return false;

    const translations = responseData.filter((item) => item.name === name);
    const existingLanguages = translations.map((t) =>
      t.language_code?.toLowerCase(),
    );
    const codes = Object.values(languageCodes);

    return codes.some(
      (code) =>
        code.toLowerCase() !== 'eng' &&
        !existingLanguages.includes(code.toLowerCase()),
    );
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ paddingBottom: '100px' }}>
        {responseData.length ? renderData() : <LoadingSpinner />}
        <EditAppInboxModal
          show={showEditMessageModal}
          onHide={() => setShowEditMessageModal(false)}
          message={messageToEdit}
          onSave={updateMessage}
        />
        <ConfirmModal
          show={showConfirmModal}
          confirm={deleteMessage}
          onHide={() => {
            setMessageToRemove(undefined);
            setShowConfirmModal(false);
          }}
          confirmMessage={confirmMessage}
          confirmTitle={confirmTitle}
        />
        <CreateAppMessageModal
          show={showAddMessageModal}
          onHide={() => setShowAddMessageModal(false)}
          onCreate={addMessage}
        />
        <MissingTranslationsModal
          open={openTranslationsModal}
          onClose={closeTranslationsModal}
          translations={translations}
          isLoading={isLoading}
          currentTranslatingLanguage={currentTranslatingLanguage}
          onAdd={async (newMessage: InboxMessageOnAzure) => {
            try {
              const addedMessage = await addInboxMessage(newMessage);

              setResponseData((prevData) => [...prevData, addedMessage]);
              showToast(
                `Successfully added ${newMessage.header} message.`,
                ToastType.SUCCESS,
              );
              return true;
            } catch (error) {
              if (error instanceof Error) {
                showToast(
                  `Failed to add ${newMessage.header} message: ${error.message}`,
                  ToastType.DANGER,
                );
              }
              return false;
            }
          }}
          currentItem={currentItem}
        />
      </Container>
      <LoadingData show={loadingAPIData} />
    </>
  );
};

export default withAuthenticationRequired(AppInbox, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
